import { useState, useEffect } from "react";

import { formatDistanceToNow } from "date-fns";
import { Fingerprint, Smartphone, Key, Trash2, Shield } from "lucide-react";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { authClient } from "@/lib/auth-client";
import { toast } from "@/components/ui/sonner";

interface Passkey {
  id: string;
  name?: string;
  createdAt: Date;
  lastUsedAt?: Date;
  deviceType?: string;
  backedUp?: boolean;
  transports?: string[];
}

export const PasskeyManager = () => {
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [isLoadingPasskeys, setIsLoadingPasskeys] = useState(true);
  const [passkeyToRemove, setPasskeyToRemove] = useState<Passkey | null>(null);
  const [isRemovingPasskey, setIsRemovingPasskey] = useState(false);

  useEffect(() => {
    loadPasskeys();
  }, []);

  const loadPasskeys = async () => {
    setIsLoadingPasskeys(true);
    try {
      // Better Auth's listPasskeys method
      const { data, error } = await authClient.passkey.listPasskeys();
      if (error) throw error;
      setPasskeys(data || []);
    } catch (error) {
      console.error("Failed to load passkeys:", error);
      toast.error("Failed to load passkeys");
    } finally {
      setIsLoadingPasskeys(false);
    }
  };

  const handleRemove = async () => {
    if (!passkeyToRemove) return;
    setIsRemovingPasskey(true);
    try {
      // Better Auth's deletePasskey method
      const { error } = await authClient.passkey.deletePasskey({
        id: passkeyToRemove.id
      });
      
      if (error) throw error;
      
      toast.success("Passkey removed successfully");
      setPasskeys(passkeys.filter(p => p.id !== passkeyToRemove.id));
      setPasskeyToRemove(null);
    } catch (error: any) {
      console.error("Failed to remove passkey:", error);
      toast.error(error.message || "Failed to remove passkey");
    } finally {
      setIsRemovingPasskey(false);
    }
  };

  const getDeviceIcon = (deviceType?: string, transports?: string[]) => {
    // Better Auth might not return transports/deviceType in the same format, adapting
    if (transports?.includes("internal") || deviceType === "platform") {
      return <Fingerprint className="h-5 w-5 text-primary" />;
    }
    if (transports?.includes("usb")) {
      return <Key className="h-5 w-5 text-blue-500" />;
    }
    return <Smartphone className="h-5 w-5 text-green-500" />;
  };

  if (isLoadingPasskeys) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Loading passkeys...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Manage Passkeys</CardTitle>
          <CardDescription>
            View and remove registered passkeys for your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {passkeys.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No passkeys registered yet</p>
              <p className="text-sm text-muted-foreground mt-2">
                Setup a passkey for faster and more secure sign-in
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {passkeys.map((passkey) => (
                <div
                  key={passkey.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    {getDeviceIcon(passkey.deviceType, passkey.transports)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{passkey.name || "Unnamed Passkey"}</p>
                        {passkey.backedUp && (
                          <Badge variant="secondary" className="text-xs">
                            Backed Up
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs text-muted-foreground">
                        <span>
                          Added {formatDistanceToNow(new Date(passkey.createdAt), { addSuffix: true })}
                        </span>
                        {passkey.lastUsedAt && (
                          <span>
                            Last used {formatDistanceToNow(new Date(passkey.lastUsedAt), { addSuffix: true })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPasskeyToRemove(passkey)}
                    disabled={isRemovingPasskey}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!passkeyToRemove} onOpenChange={(open) => !open && setPasskeyToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Passkey?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{passkeyToRemove?.name || "this passkey"}"? You'll need to register it again to use it for authentication.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              disabled={isRemovingPasskey}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemovingPasskey ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
